/**
 * Notifications API endpoints
 *
 * This module contains all API endpoint functions related to notifications.
 */

import { apiClient } from './client';

/**
 * Notification type
 */
export type NotificationType = 'QUOTE_STATUS_CHANGED';

/**
 * Entity type
 */
export type EntityType = 'QUOTE';

/**
 * Changed by user info
 */
export interface ChangedByUser {
  id: string;
  name: string;
}

/**
 * Notification data snapshot
 */
export interface NotificationData {
  quoteReferenceNumber: string;
  oldStatus: string;
  newStatus: string;
  changedBy: ChangedByUser;
}

/**
 * Notification item
 */
export interface Notification {
  id: string;
  notificationId: string;
  userId: string;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  notification: {
    id: string;
    type: NotificationType;
    title: string;
    message: string;
    entityType: EntityType | null;
    entityId: string | null;
  };
}

/**
 * Unread count response
 */
export interface UnreadCountResponse {
  count: number;
}

/**
 * List notifications params
 */
export interface ListNotificationsParams {
  page?: number;
  limit?: number;
}

/**
 * List notifications response
 */
export interface ListNotificationsResponse {
  notifications: Notification[];
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * Mark as read response
 */
export interface MarkAsReadResponse {
  message: string;
}

/**
 * Mark all as read response
 */
export interface MarkAllAsReadResponse {
  message: string;
}

/**
 * Delete notification response
 */
export interface DeleteNotificationResponse {
  message: string;
}

/**
 * List notifications with pagination
 */
export async function listNotifications(
  params?: ListNotificationsParams
): Promise<ListNotificationsResponse> {
  const queryParams: Record<string, string | number | boolean | undefined> = {};
  if (params) {
    if (params.page !== undefined) queryParams.page = params.page;
    if (params.limit !== undefined) queryParams.limit = params.limit;
  }

  const response = await apiClient.get<{
    success: boolean;
    data: Notification[];
    meta?: {
      pagination?: {
        page: number;
        limit: number;
        total_items: number;
        total_pages: number;
        has_next: boolean;
        has_prev: boolean;
      };
    };
  }>('/api/notifications', { params: queryParams, unwrapData: false });

  const pagination = response.meta?.pagination;
  return {
    notifications: (Array.isArray(response.data) ? response.data : []) || [],
    pagination: {
      page: pagination?.page ?? params?.page ?? 1,
      limit: pagination?.limit ?? params?.limit ?? 10,
      totalItems: pagination?.total_items ?? 0,
      totalPages: pagination?.total_pages ?? 1,
      hasNext: pagination?.has_next ?? false,
      hasPrev: pagination?.has_prev ?? false,
    },
  };
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(): Promise<{ count: number }> {
  return apiClient.get<UnreadCountResponse>(
    '/api/notifications/unread-count'
  );
}

/**
 * Mark a notification as read
 */
export async function markAsRead(id: string): Promise<{ message: string }> {
  return apiClient.patch<{ success: boolean; data: MarkAsReadResponse }>(
    `/api/notifications/${id}/read`
  ).then((res) => res.data);
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<{ message: string }> {
  return apiClient.patch<{ success: boolean; data: MarkAllAsReadResponse }>(
    '/api/notifications/read-all'
  ).then((res) => res.data);
}

/**
 * Delete a notification
 */
export async function deleteNotification(id: string): Promise<{ message: string }> {
  return apiClient.delete<{ success: boolean; data: DeleteNotificationResponse }>(
    `/api/notifications/${id}`
  ).then((res) => res.data);
}
